/**
 * Inline message block — the `.banner` family from the vanilla pages.
 * Used for load errors (with the CMDBuild hint), scan results, plan parse
 * errors and the non-production data marker.
 */

import type { ReactNode } from 'react';
import { Icon } from './Icon';

export type BannerKind = 'info' | 'error' | 'ok' | 'warn' | 'demo';

export interface BannerProps {
  /** Visual variant; `demo` marks a non-production data source. */
  kind?: BannerKind;
  /** Main message. German, and server messages are shown verbatim. */
  children: ReactNode;
  /** Optional muted second line, e.g. the CMDBuild troubleshooting hint. */
  sub?: ReactNode;
  /** Optional bullet list, e.g. the error array of a `partial` response. */
  items?: readonly ReactNode[];
  /** Extra class names. */
  className?: string;
  /** Renders a close button that calls this handler. */
  onDismiss?: () => void;
}

export function Banner({
  kind = 'info',
  children,
  sub,
  items,
  className,
  onDismiss,
}: BannerProps): ReactNode {
  const classes = ['banner'];
  if (kind !== 'info') classes.push(kind);
  if (className) classes.push(className);
  return (
    <div className={classes.join(' ')} role={kind === 'error' ? 'alert' : 'status'}>
      <div className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        {onDismiss ? (
          <button type="button" className="close-x" onClick={onDismiss} aria-label="Meldung schließen">
            <Icon name="close" />
          </button>
        ) : null}
      </div>
      {sub ? <div className="banner-sub">{sub}</div> : null}
      {items && items.length > 0 ? (
        <ul>
          {items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default Banner;
